/**
 * Claw9 Subscription Tier Helper Utilities
 * Determines which features are available based on subscription tier
 */

const TIER_HIERARCHY = {
    'free': 0,
    'basic': 1,      // Claw9-Basic - includes Claw9 Chat & CRM
    'pro': 2,
    'business': 3,
    'enterprise': 4
};

export const TEAM_LIMITS = {
    'free': 0,
    'basic': 3,
    'pro': 10,
    'business': 50,
    'enterprise': 999
};

/**
 * Normalize tier name to lowercase for comparison
 */
export const normalizeTier = (tier) => {
    if (!tier) return 'basic'; // Default to basic for Claw9
    return String(tier).toLowerCase();
};

/**
 * Check if a feature is available for the given subscription tier
 * @param {string} userTier - User's subscription tier
 * @param {string} requiredTier - Minimum tier required for feature
 * @returns {boolean}
 */
export const hasFeatureAccess = (userTier, requiredTier) => {
    const normalizedUserTier = normalizeTier(userTier);
    const normalizedRequiredTier = normalizeTier(requiredTier);

    const userLevel = TIER_HIERARCHY[normalizedUserTier] ?? 1; // Default to basic
    const requiredLevel = TIER_HIERARCHY[normalizedRequiredTier] ?? 0;

    return userLevel >= requiredLevel;
};

/**
 * Get feature availability for a subscription
 * For Claw9, the Basic tier already includes Chat and CRM
 * @param {object} subscription - Subscription data
 * @returns {object} Feature flags
 */
export const getFeatureFlags = (subscription) => {
    // Extract tier from various possible locations
    const possibleTiers = [
        subscription?.tier,
        subscription?.subscription,
        subscription?.plan?.tier,
        subscription?.plan?.name,
        subscription?.user?.subscription,
        subscription?.data?.tier,
    ].filter(Boolean).map(normalizeTier);

    // Use the highest tier found, default to basic
    let tier = 'basic';
    let highestLevel = 1;

    for (const t of possibleTiers) {
        const level = TIER_HIERARCHY[t] ?? 1;
        if (level > highestLevel) {
            highestLevel = level;
            tier = t;
        }
    }

    return {
        // Infrastructure Dashboard (all tiers)
        hasInfrastructure: true,

        // Claw9 Chat (Basic+ - included by default)
        hasClaw9Chat: hasFeatureAccess(tier, 'basic'),

        // Claw9 CRM (Basic+ - included by default)
        hasClaw9CRM: hasFeatureAccess(tier, 'basic'),

        // Claw9 Agents (Basic+ - included by default)
        hasClaw9Agents: hasFeatureAccess(tier, 'basic'),

        // Combined check for Chat & CRM
        hasClaw9: hasFeatureAccess(tier, 'basic'),

        // Workspace Management (Basic+)
        hasWorkspaces: hasFeatureAccess(tier, 'basic'),

        // Template Gallery (Basic+)
        hasTemplates: hasFeatureAccess(tier, 'basic'),

        // Backups (Pro+)
        hasBackups: hasFeatureAccess(tier, 'pro'),

        // Agent Manager (Pro+)
        hasAgentManager: hasFeatureAccess(tier, 'pro'),

        // Advanced Analytics (Business+)
        hasAdvancedAnalytics: hasFeatureAccess(tier, 'business'),

        // API Access (Business+)
        hasApiAccess: hasFeatureAccess(tier, 'business'),

        // White Label (Enterprise)
        hasWhiteLabel: hasFeatureAccess(tier, 'enterprise'),

        // Custom Integrations (Enterprise)
        hasCustomIntegrations: hasFeatureAccess(tier, 'enterprise'),

        // Tier checks
        isBasic: tier === 'basic',
        isPro: hasFeatureAccess(tier, 'pro'),
        isBusiness: hasFeatureAccess(tier, 'business'),
        isEnterprise: hasFeatureAccess(tier, 'enterprise'),

        // Current tier name (capitalized)
        tierName: tier.charAt(0).toUpperCase() + tier.slice(1),
        tier: tier,
    };
};

/**
 * Get upgrade message for a locked feature
 * @param {string} featureName - Name of the feature
 * @param {string} requiredTier - Minimum tier required
 * @returns {string}
 */
export const getUpgradeMessage = (featureName, requiredTier = 'pro') => {
    const tierName = requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1);
    return `${featureName} is available on ${tierName} plan and above. Upgrade to unlock this feature.`;
};

/**
 * Get team member limit for a tier
 */
export const getTeamLimit = (tier) => {
    const normalized = normalizeTier(tier);
    return TEAM_LIMITS[normalized] ?? 3;
};
